import { createContext, type ReactNode, useContext, useMemo } from 'react';

import { COOKIE_KEYS } from '@moniebook-web/types';
import { getItem } from '@moniebook-web/utils';

import { GHOST_TOKEN_SCOPE } from '../constants';
import type { GhostTokenPayload } from '../types';
import { decodeJwtPayload } from '../utils';

interface SessionTokenPayloadContextValue {
    isGhostSession: boolean;
    sessionStartsAt: number | null;
    sessionExpiresAt: number | null;
    sessionId: string | null;
    staffUserId: string | null;
    businessId: string | null;
}

const DEFAULT_VALUE: SessionTokenPayloadContextValue = {
    isGhostSession: false,
    sessionStartsAt: null,
    sessionExpiresAt: null,
    sessionId: null,
    staffUserId: null,
    businessId: null,
};

const SessionTokenPayloadContext =
    createContext<SessionTokenPayloadContextValue>(DEFAULT_VALUE);

export function SessionTokenPayloadProvider({
    children,
}: Readonly<{ children: ReactNode }>) {
    const value = useMemo<SessionTokenPayloadContextValue>(() => {
        const token = getItem(COOKIE_KEYS.TOKEN_VARIABLE);
        const payload = token
            ? decodeJwtPayload<GhostTokenPayload>(token)
            : null;

        if (payload?.scope !== GHOST_TOKEN_SCOPE) {
            return DEFAULT_VALUE;
        }

        return {
            isGhostSession: true,
            sessionId: payload.ghost_session_id,
            sessionStartsAt: payload.session_starts_at,
            sessionExpiresAt: payload.exp * 1000,
            staffUserId: payload.staff_user_id,
            businessId: payload.business_id,
        };
    }, []);

    return (
        <SessionTokenPayloadContext.Provider value={value}>
            {children}
        </SessionTokenPayloadContext.Provider>
    );
}

export const useSessionTokenPayload = () =>
    useContext(SessionTokenPayloadContext);
